import { $$ } from "@tento-ui/fastdom";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { ArrowsAngleExpandIcon, LonaIcons } from "@tento-ui/ui/icons";
import { Rational } from "@tento-core/rational";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { type Option, RowId } from "@tento-lona/sheets";
import type { RowLocalId } from "@tento-lona/sheets";
import { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";

/**
 * Content-agnostic "expand a clipped cell into a full-width popup" controller.
 *
 * A cell composes one of these, handing it the expand button, an anchor getter,
 * a horizontal-clip probe, and a popup factory. The controller owns the generic
 * mechanism — batched x-axis-clip detection, the expand-button state machine,
 * row-aligned `fixedChild` reveal geometry, the hidden-first flash-avoidance,
 * and dismissal across rebind/recycle. It is a pure view-state transform: the
 * popup renders whatever content the cell supplies, so edit-vs-read-only is
 * inherited from that content, never imposed here.
 *
 * Extracted from the text-only expand logic that previously lived inside
 * `TextCell`.
 */
export class CellExpander {
  #expandable: boolean = false;
  #popupId: Option<string> = null;
  /** Handle captured at reveal time so dismissal still works after the cell's
   *  anchor has been cleared (rebind/recycle). */
  #activeHandle: Option<SheetDelegate> = null;
  #anchorKey: Option<string> = null;

  // One shared read/write phase for ALL expanders (text + json + …): a single
  // `$$.measure` reads every pending expander's clip state, then a single
  // `$$.mutate` applies the button state — no per-cell layout thrash.
  static #pending = new Set<CellExpander>();
  static #batchQueued = false;

  readonly #config: CellExpander.Config;
  constructor(config: CellExpander.Config) {
    this.#config = config;
    GESTURE_MANAGER.addPointerEvent(config.$expandBtn, {
      onClick: () => void this.#showExpanded(),
    });
  }

  /** Re-evaluate clip state now and across the next few paints — content (an
   *  editor view, a JSON list) may not have laid out on the bind frame. */
  refresh(): void {
    this.#queueCheck();
    this.#queueCheckAfterPaint(3);
  }

  /** Resize keeps the content mounted. The shared read phase runs after the
   *  column bind writes its width, so it needs one clip check, not mount retries. */
  refreshAfterResize(): void {
    this.#queueCheck();
  }

  /** Call from the cell's `bind` after its anchor is set: dismiss a stale popup
   *  if the cell was reused for a different (row, column), then re-check clip. */
  onRebind(): void {
    const key = this.#currentAnchorKey();
    if (this.#popupId != null && key !== this.#anchorKey) {
      this.dismiss();
    }
    this.#anchorKey = key;
    this.refresh();
  }

  /** Call from the cell's `onRecycle`. */
  onRecycle(): void {
    this.dismiss();
    CellExpander.#pending.delete(this);
    this.#applyExpandState(false);
    this.#anchorKey = null;
  }

  dismiss(): void {
    const popupId = this.#popupId;
    if (popupId == null) return;
    this.#activeHandle?.fixedChild.dismiss(popupId, "instant");
    this.#popupId = null;
    this.#activeHandle = null;
  }

  #currentAnchorKey(): Option<string> {
    const anchor = this.#config.getAnchor();
    return anchor == null
      ? null
      : `${anchor.rowId.toString()}|${anchor.column.toString()}`;
  }

  #queueCheckAfterPaint(remainingFrames: number): void {
    if (remainingFrames <= 0) return;
    $$.deferMutate(() => {
      this.#queueCheck();
      this.#queueCheckAfterPaint(remainingFrames - 1);
    });
  }

  #queueCheck(): void {
    CellExpander.#pending.add(this);
    if (CellExpander.#batchQueued) return;
    CellExpander.#batchQueued = true;
    $$.measure(() => {
      const results = new Map<CellExpander, boolean>();
      for (const expander of CellExpander.#pending) {
        results.set(expander, expander.#isExpandable());
      }
      CellExpander.#pending.clear();
      CellExpander.#batchQueued = false;
      $$.mutate(() => {
        for (const [expander, expandable] of results) {
          expander.#applyExpandState(expandable);
        }
      });
    });
  }

  #isExpandable(): boolean {
    return this.#config.getAnchor() != null && this.#config.isClipped();
  }

  #applyExpandState(expandable: boolean): void {
    const next = expandable && this.#config.getAnchor() != null;
    if (this.#expandable === next) return;
    this.#expandable = next;

    this.#config.$expandBtn.hidden = !next;
    this.#config.host.toggleAttribute("expandable", next);
    if (next) {
      this.#config.$expandBtn.replaceChildren(
        LonaIcons.$svg(ArrowsAngleExpandIcon),
      );
    }
  }

  async #showExpanded(): Promise<void> {
    const anchor = this.#config.getAnchor();
    if (anchor == null) return;

    const popupId = `${this.#config.popupIdPrefix}-${anchor.rowId.toString()}`;
    this.#popupId = popupId;
    this.#activeHandle = anchor.handle;

    const rowHeight =
      anchor.handle.rebind.sheet.layout.rowLayout.get(anchor.localId)
        ?.cellHeightPx ?? this.#config.host.offsetHeight;
    const popupHeight = Math.max(1, rowHeight - 2);

    const $popup = await this.#config.makePopup({
      heightPx: popupHeight,
      popupId,
      requestDismiss: () => {
        if (this.#popupId !== popupId) return;
        this.dismiss();
      },
    });
    // A rebind/recycle during makePopup's await supersedes this reveal.
    if (this.#popupId !== popupId) return;

    const subindex = new Rational(0, 1);
    anchor.handle.fixedChild.reveal(
      popupId,
      $popup,
      {
        x: anchor.column.withSubindex(subindex),
        y: anchor.localId,
      },
      {
        widthBehavior: "fill-right",
        animate: false,
        inset: { left: 0, top: -2 },
      },
    );

    await $popup.waitForLayout();
    if (this.#popupId !== popupId) return;
    $popup.revealAfterLayout();
    $popup.focusContent();
  }
}

export namespace CellExpander {
  /** Where the popup anchors and how tall the row is. */
  export interface Anchor {
    handle: SheetDelegate;
    column: ColumnIndex.Key.Resolved.Default;
    rowId: RowId;
    localId: RowLocalId;
  }

  /** Passed to `makePopup` so the adapter can wire dismissal back to us. */
  export interface PopupContext {
    heightPx: number;
    popupId: string;
    requestDismiss: () => void;
  }

  /** The minimal popup contract the controller drives. `CellExpandPopup`
   *  satisfies it; an adapter could supply anything structurally equivalent. */
  export interface Popup extends HTMLElement {
    waitForLayout(): Promise<void>;
    revealAfterLayout(): void;
    focusContent(): void;
  }

  export interface Config {
    /** The cell host — used for the `[expandable]` attribute + height fallback. */
    host: HTMLElement;
    /** The expand button living in the cell's template. */
    $expandBtn: HTMLElement;
    /** Current anchor, or null when the cell is unbound/loading. */
    getAnchor: () => Option<Anchor>;
    /** True when the cell content is clipped on the x-axis. */
    isClipped: () => boolean;
    /** Build + bind the full-width popup for the current content. */
    makePopup: (ctx: PopupContext) => Promise<Popup>;
    /** Prefix for the per-row `fixedChild` slot id, e.g. "text-popup". */
    popupIdPrefix: string;
  }
}
